// A photo full screen over the app (#393): × , tap beside the photo, swipe
// down or Escape to close back to the same place in the form; swipe or the
// arrows to move along the strip it was opened from.
import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { ChevronLeft, ChevronRight, ImageOff, Loader2, Trash2, X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@higgins/ui';
import type { ShownPhoto } from '../lib/area-photos';
import { useOnline } from '../lib/hooks';
import { previewNote, stepIndex, swipeAction, type FullSize } from '../lib/photo-preview';
import { useFullSizePhoto, usePhotoThumbnail } from '../lib/use-photo-images';
import { PhotoDetails } from './photo-details';

/** A tap moves less than this (px); anything further is a drag. */
const TAP_PX = 10;

const roundButton = 'flex h-11 w-11 items-center justify-center rounded-full bg-white/90 text-slate-900 shadow';

/** The picture: full size when it's here, else the thumbnail enlarged, else a placeholder. */
function PreviewPicture({ photo }: { photo: ShownPhoto }) {
  const online = useOnline();
  const loaded = useFullSizePhoto(photo, online);
  const thumbnail = usePhotoThumbnail(photo);
  const [broken, setBroken] = useState(false);
  const full: FullSize = broken ? { status: 'unavailable', reason: 'failed' } : loaded;
  const note = previewNote(full, Boolean(thumbnail));
  const src = full.status === 'ready' ? full.url : thumbnail;
  const pictureClass = 'max-h-full max-w-full select-none object-contain [-webkit-touch-callout:none]';

  return (
    <>
      {src ? (
        <img src={src} alt={photo.fileName} draggable={false} onError={() => full.status === 'ready' && setBroken(true)} className={pictureClass} />
      ) : (
        <div className="flex flex-col items-center gap-2 text-white/70">
          {full.status === 'loading' ? <Loader2 className="h-8 w-8 animate-spin" /> : <ImageOff className="h-10 w-10" />}
        </div>
      )}
      {full.status === 'loading' && src && (
        <span className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-sm text-white">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading full size
        </span>
      )}
      {note && <p className="pointer-events-none absolute inset-x-4 bottom-3 text-center text-sm text-white/85">{note}</p>}
    </>
  );
}

interface PreviewProps {
  photos: ShownPhoto[];
  /** The photo showing, by its place in `photos`; null = closed. */
  index: number | null;
  onIndex: (index: number | null) => void;
  /** Remove a photo, after the measurer confirms; only a photo with a row can be removed. */
  onRemove?: (photo: ShownPhoto) => void;
}

/** Remove, then a second tap to confirm, under the photo. */
function RemoveBar({ photo, onRemove, onDone }: { photo: ShownPhoto; onRemove: (photo: ShownPhoto) => void; onDone: () => void }) {
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return (
      <button type="button" onClick={() => setConfirming(true)} className="flex min-h-11 items-center gap-2 rounded-full bg-white/90 px-4 text-sm font-medium text-red-700 shadow">
        <Trash2 className="h-4 w-4" /> Remove photo
      </button>
    );
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-3 rounded-lg bg-white/95 px-4 py-2 text-sm text-slate-900 shadow">
      <span>Remove {photo.fileName.replace(/\.jpg$/i, '')}? It goes to the SharePoint recycle bin for 93 days.</span>
      <button
        type="button"
        onClick={() => {
          onRemove(photo);
          onDone();
        }}
        className="min-h-11 rounded-md bg-red-700 px-4 font-medium text-white"
      >
        Remove
      </button>
      <button type="button" onClick={() => setConfirming(false)} className="min-h-11 rounded-md border border-slate-300 px-4">
        Keep it
      </button>
    </div>
  );
}

export function PhotoPreview({ photos, index, onIndex, onRemove }: PreviewProps) {
  const start = useRef<{ x: number; y: number; onBackdrop: boolean } | null>(null);
  const photo = index === null ? undefined : photos[index];
  const count = photos.length;

  function step(by: number) {
    if (index !== null) onIndex(stepIndex(index, by, count));
  }

  function pointerDown(event: PointerEvent<HTMLDivElement>) {
    start.current = { x: event.clientX, y: event.clientY, onBackdrop: event.target === event.currentTarget };
  }

  function pointerUp(event: PointerEvent<HTMLDivElement>) {
    const from = start.current;
    start.current = null;
    if (!from) return;
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    const isTap = Math.abs(dx) < TAP_PX && Math.abs(dy) < TAP_PX;
    if (isTap) {
      if (from.onBackdrop) onIndex(null);
      return;
    }
    const action = swipeAction(dx, dy);
    if (action === 'close') onIndex(null);
    else if (action === 'next') step(1);
    else if (action === 'previous') step(-1);
  }

  function keyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowRight') step(1);
    else if (event.key === 'ArrowLeft') step(-1);
  }

  return (
    <Dialog open={Boolean(photo)} onOpenChange={(open) => !open && onIndex(null)}>
      {photo && index !== null && (
        <DialogContent
          aria-describedby={undefined}
          onKeyDown={keyDown}
          className="inset-0 flex h-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none bg-black p-0 text-white shadow-none sm:rounded-none [&>button:last-child]:hidden"
        >
          <header className="flex items-center gap-3 px-4 pb-2 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <DialogTitle className="min-w-0 flex-1 truncate text-base font-medium text-white">{photo.fileName}</DialogTitle>
            {count > 1 && (
              <span className="shrink-0 text-sm text-white/70">
                {index + 1} of {count}
              </span>
            )}
            <DialogClose aria-label="Close the photo" className={roundButton}>
              <X className="h-6 w-6" />
            </DialogClose>
          </header>
          <div
            onPointerDown={pointerDown}
            onPointerUp={pointerUp}
            onPointerCancel={() => (start.current = null)}
            className="relative flex min-h-0 flex-1 touch-pinch-zoom items-center justify-center px-2 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
          >
            <PreviewPicture key={photo.id} photo={photo} />
            {index > 0 && (
              <button type="button" onClick={() => step(-1)} aria-label="Previous photo" className={`${roundButton} absolute left-3 top-1/2 -translate-y-1/2`}>
                <ChevronLeft className="h-6 w-6" />
              </button>
            )}
            {index < count - 1 && (
              <button type="button" onClick={() => step(1)} aria-label="Next photo" className={`${roundButton} absolute right-3 top-1/2 -translate-y-1/2`}>
                <ChevronRight className="h-6 w-6" />
              </button>
            )}
          </div>
          {photo.row && (
            <div className="flex flex-col items-center gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <PhotoDetails key={photo.id} photo={{ ...photo, row: photo.row }} />
              {onRemove && <RemoveBar key={`remove-${photo.id}`} photo={photo} onRemove={onRemove} onDone={() => onIndex(null)} />}
            </div>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
